/* eslint-disable react/function-component-definition */
import React, { useContext, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSearch } from '@fortawesome/fontawesome-free-solid';
import { toast } from 'react-toastify';
import { Row, Col, Card, Badge, ProgressBar, Button, Image } from 'react-bootstrap';
import axios from 'axios';
import styles from './findSimilarCafesModal.module.scss';
import { TableContext } from '../../contexts/tableContext';
import envValues from '../../enviornment';
import { getCookies } from '../../hooks/useCookies';
import TextInput from '../../components/TextField/TextField';
import { useForm } from 'react-hook-form';
import Loader from '../../components/Loader/Loader';

const FindSimilarCafesModal = React.memo(({ show, handleClose, cafeId, onSelectCafe }) => {
  const { formthemeStyle } = useContext(TableContext);
  const [loading, setLoading] = useState(false);
  const [cafe, setCafe] = useState(null);
  const [similarCafes, setSimilarCafes] = useState([]);
  const [filteredCafes, setFilteredCafes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const token = `Bearer ${getCookies('Token')}`;
  const fetchedRef = React.useRef(false);

  const { register, formState: { errors } } = useForm();

  useEffect(() => {
    // Prevent double fetch in React Strict Mode
    if (show && cafeId && !fetchedRef.current) {
      fetchedRef.current = true;
      fetchSimilarCafes();
    }
    
    // Reset ref when modal closes
    if (!show) {
      fetchedRef.current = false;
    }
  }, [show, cafeId]);

  useEffect(() => {
    // Filter cafes based on search term
    if (searchTerm.trim() === '') {
      setFilteredCafes(similarCafes);
    } else {
      const filtered = similarCafes.filter(c =>
        c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.location_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredCafes(filtered);
    }
  }, [searchTerm, similarCafes]);

  const fetchSimilarCafes = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${envValues.REACT_APP_API_ENDPOINT}/cafe/similar/${cafeId}?limit=20`,
        {
          headers: { Authorization: token },
        }
      );

      if (response.data.success) {
        setCafe(response.data.data.cafe);
        // API returns 'cafes' not 'matches'
        const cafes = response.data.data.cafes || response.data.data.matches || [];
        setSimilarCafes(cafes);
        setFilteredCafes(cafes);
        
        if (cafes.length === 0) {
          toast.info('No similar cafes found');
        }
      } else {
        toast.error('Failed to load similar cafes');
      }
    } catch (error) {
      console.error('Error fetching similar cafes:', error);
      toast.error('Failed to load similar cafes');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectCafe = (selectedCafe) => {
    if (onSelectCafe) {
      onSelectCafe(selectedCafe);
    }
    handleClose();
  };

  const getMatchScoreColor = (score) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'info';
    if (score >= 40) return 'warning';
    return 'danger';
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      Active: 'success',
      Unclaimed: 'warning',
      Pending: 'info',
      Inactive: 'secondary',
    };
    return statusMap[status] || 'secondary';
  };

  if (!show) return null;

  return (
    <div className={styles.popupbox}>
      <div className={styles.box}>
        <button
          className={styles.closeicon}
          aria-label="Close"
          onClick={handleClose}
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
        
        <h2 className={styles.heading}>Find Similar Cafes</h2>
        
        {cafe && (
          <div className={styles.currentCafe}>
            <strong>Current Cafe:</strong> {cafe.name}
            {cafe.address && <span className={styles.address}> - {cafe.address}</span>}
          </div>
        )}

        <div className={styles.searchBox}>
          <FontAwesomeIcon icon={faSearch} className={styles.searchIcon} />
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Search by name, address, or location..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className={styles.content}>
          {loading ? (
            <div className={styles.loadingContainer}>
              <Loader />
              <p>Finding similar cafes...</p>
            </div>
          ) : filteredCafes.length === 0 ? (
            <div className={styles.emptyState}>
              <p>{searchTerm ? 'No cafes match your search' : 'No similar cafes found'}</p>
            </div>
          ) : (
            <div className={styles.cafeList}>
              <p className={styles.resultCount}>
                Found {filteredCafes.length} similar cafe{filteredCafes.length !== 1 ? 's' : ''}
              </p>
              
              {filteredCafes.map((similarCafe) => (
                <Card key={similarCafe._id} className={styles.cafeCard}>
                  <Card.Body>
                    <Row>
                      <Col xs={3} md={2}>
                        {similarCafe.image ? (
                          <Image
                            src={similarCafe.image}
                            alt={similarCafe.name}
                            className={styles.cafeImage}
                            fluid
                            rounded
                          />
                        ) : (
                          <div className={styles.placeholderImage}>
                            <span>{similarCafe.name?.charAt(0) || '?'}</span>
                          </div>
                        )}
                      </Col>
                      
                      <Col xs={9} md={7}>
                        <h5 className={styles.cafeName}>{similarCafe.name}</h5>
                        
                        <div className={styles.cafeDetails}>
                          <p className={styles.address}>{similarCafe.address || 'No address'}</p>
                          {similarCafe.location_name && (
                            <p className={styles.location}>
                              <small>{similarCafe.location_name}</small>
                            </p>
                          )}
                        </div>
                        
                        <div className={styles.badges}>
                          <Badge bg={getStatusBadge(similarCafe.cafeStatus)}>
                            {similarCafe.cafeStatus}
                          </Badge>
                          {similarCafe.isClaimed && (
                            <Badge bg="primary">Claimed</Badge>
                          )}
                          {similarCafe.vendor && (
                            <Badge bg="info">
                              Vendor: {similarCafe.vendor.name}
                            </Badge>
                          )}
                        </div>

                        <div className={styles.matchReasons}>
                          <small className={styles.reasonLabel}>Matches:</small>
                          {similarCafe.matchReasons.map((reason, idx) => (
                            <Badge
                              key={idx}
                              bg="secondary"
                              className={styles.reasonBadge}
                            >
                              {reason}
                            </Badge>
                          ))}
                        </div>
                      </Col>
                      
                      <Col xs={12} md={3} className={styles.scoreColumn}>
                        <div className={styles.matchScore}>
                          <div className={styles.scoreValue}>
                            {similarCafe.matchScore}%
                          </div>
                          <ProgressBar
                            now={similarCafe.matchScore}
                            variant={getMatchScoreColor(similarCafe.matchScore)}
                            className={styles.scoreBar}
                          />
                          <small className={styles.scoreLabel}>Match Score</small>
                        </div>
                        
                        <Button
                          variant="primary"
                          size="sm"
                          className={styles.selectButton}
                          onClick={() => handleSelectCafe(similarCafe)}
                        >
                          Suggest Merge
                        </Button>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

export default FindSimilarCafesModal;
